import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import { chatApiClient } from '../../chatApiClient';
import { supportCasesApiClient } from '../../supportCasesApiClient';
import { ChatSummary } from '../../models/Chat';
import { SupportCase } from '../../models/SupportCase';
import {
  formatChatTimestamp,
  chatAvatarColor,
  chatBasePath,
  chatInboxFromPath,
  chatMemberCountLabel,
  chatParticipants,
  chatPartnerLabel,
  chatCounterpartyUserId,
  chatTitleLabel,
  compareChatListOrder,
  isOrganizationScopedChat,
  isOrganizationSupportChat,
  isSupportCaseChat,
  isSoloOrganizationStaffChat,
  isPinnedChat,
  isAdminChatInbox,
} from '../../utils/chat';
import { supportCaseByChatId, supportCaseSubjectByChatId } from '../../utils/supportCases';
import SupportCaseStatusChip from '../../components/chat/SupportCaseStatusChip';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import {
  loadEligibleChatPartnerOptions,
  ChatPartnerOption,
} from '../../utils/chatPartners';
import { buildChatAvatarMap, chatAvatarSrc } from '../../utils/chatAvatars';
import { chatPageSx } from '../../components/chat/chatLayout';
import ChatPartnerPicker from '../../components/chat/ChatPartnerPicker';
import { ChatMemberAvatars } from '../../components/chat/ChatMemberAvatars';
import ChatUnreadBadge from '../../components/chat/ChatUnreadBadge';
import { chatUnreadRowSx } from '../../components/chat/chatUnreadBadgeStyles';
import { authService } from '../../services/AuthService';
import { getCachedProfileDisplay } from '../../services/profileDisplayCache';

type AdminChatTab = 'userSupport' | 'orgSupport';

const ChatListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = chatBasePath(location.pathname);
  const inbox = chatInboxFromPath(location.pathname);
  const adminInbox = isAdminChatInbox(location.pathname);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [supportCases, setSupportCases] = useState<SupportCase[]>([]);
  const [adminTab, setAdminTab] = useState<AdminChatTab>('userSupport');
  const currentUserId = authService.getUserId() ?? undefined;
  const currentProfileImageUrl = getCachedProfileDisplay()?.profileImageUrl;
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<ChatPartnerOption | null>(null);
  const [partnerOptions, setPartnerOptions] = useState<ChatPartnerOption[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [supportCaseOpen, setSupportCaseOpen] = useState(false);
  const [supportSubject, setSupportSubject] = useState('');
  const [supportMessage, setSupportMessage] = useState('');
  const [creatingSupportCase, setCreatingSupportCase] = useState(false);
  const [supportCaseError, setSupportCaseError] = useState<string | null>(null);

  const loadChats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, cases] = await Promise.all([
        chatApiClient.listChats(inbox),
        supportCasesApiClient.listSupportCases().catch(() => [] as SupportCase[]),
      ]);
      setChats(list);
      setSupportCases(cases);
    } catch {
      setError(t('chat.list.loadError', 'Failed to load chats'));
    } finally {
      setLoading(false);
    }
  }, [t, inbox]);

  useEffect(() => {
    void loadChats();
  }, [loadChats]);

  const sortedChats = useMemo(
    () =>
      [...chats]
        .filter((chat) => !isSoloOrganizationStaffChat(chat, currentUserId))
        .sort(compareChatListOrder),
    [chats, currentUserId]
  );

  const adminUserSupportChats = useMemo(
    () => sortedChats.filter((chat) => isSupportCaseChat(chat)),
    [sortedChats]
  );

  const adminOrgSupportChats = useMemo(
    () => sortedChats.filter((chat) => isOrganizationSupportChat(chat)),
    [sortedChats]
  );

  const visibleChats = useMemo(() => {
    if (!adminInbox) return sortedChats;
    return adminTab === 'userSupport' ? adminUserSupportChats : adminOrgSupportChats;
  }, [adminInbox, adminTab, sortedChats, adminUserSupportChats, adminOrgSupportChats]);

  const adminUserSupportUnread = useMemo(
    () => adminUserSupportChats.reduce((sum, chat) => sum + (chat.unreadCount ?? 0), 0),
    [adminUserSupportChats]
  );

  const adminOrgSupportUnread = useMemo(
    () => adminOrgSupportChats.reduce((sum, chat) => sum + (chat.unreadCount ?? 0), 0),
    [adminOrgSupportChats]
  );

  const avatarByUserId = useMemo(
    () => buildChatAvatarMap(chats, currentUserId, currentProfileImageUrl),
    [chats, currentUserId, currentProfileImageUrl]
  );

  const loadPartnerOptions = useCallback(async () => {
      if (!currentUserId || adminInbox) return;

      setPartnersLoading(true);
      setPartnersError(null);
      setSelectedPartner(null);

      try {
        const options = await loadEligibleChatPartnerOptions(chats, currentUserId);
        setPartnerOptions(options);
      } catch {
        setPartnerOptions([]);
        setPartnersError(t('chat.create.partnersLoadError', 'Failed to load eligible contacts.'));
      } finally {
        setPartnersLoading(false);
      }
    },
    [currentUserId, t, chats, adminInbox]
  );

  const openCreateDialog = () => {
    setSelectedPartner(null);
    setPartnerOptions([]);
    setPartnersError(null);
    setCreateError(null);
    setCreateOpen(true);
  };

  useEffect(() => {
    if (!createOpen || !currentUserId) return;
    void loadPartnerOptions();
  }, [createOpen, currentUserId, loadPartnerOptions]);

  const handleCreate = async () => {
    if (!currentUserId) {
      setCreateError(t('chat.create.profileRequired', 'Load your profile before starting a chat.'));
      return;
    }
    if (!selectedPartner) {
      setCreateError(t('chat.create.partnerRequired', 'Select a recipient.'));
      return;
    }

    const request = {
      participantOneUserId: currentUserId,
      participantTwoUserId: selectedPartner.userId,
    };

    setCreating(true);
    setCreateError(null);
    try {
      const created = await chatApiClient.createChat(request);
      if (!created.id) {
        setCreateError(t('chat.create.error', 'Failed to start chat.'));
        return;
      }
      setCreateOpen(false);
      navigate(`${basePath}/${created.id}`);
    } catch {
      setCreateError(t('chat.create.error', 'Failed to start chat.'));
    } finally {
      setCreating(false);
    }
  };

  const openSupportCaseDialog = () => {
    setSupportSubject('');
    setSupportMessage('');
    setSupportCaseError(null);
    setSupportCaseOpen(true);
  };

  const handleCreateSupportCase = async () => {
    const subject = supportSubject.trim();
    const initialMessage = supportMessage.trim();
    if (!subject) {
      setSupportCaseError(t('supportCases.create.subjectRequired', 'Enter a subject for your request.'));
      return;
    }
    if (!initialMessage) {
      setSupportCaseError(t('supportCases.create.messageRequired', 'Enter a message describing your request.'));
      return;
    }

    setCreatingSupportCase(true);
    setSupportCaseError(null);
    try {
      const created = await supportCasesApiClient.createSupportCase({
        subject,
        initialMessage,
      });
      setSupportCaseOpen(false);
      setSupportCases((prev) => [created, ...prev.filter((entry) => entry.id !== created.id)]);
      navigate(`${basePath}/${created.chatId}`);
    } catch {
      setSupportCaseError(t('supportCases.create.error', 'Failed to create support case.'));
    } finally {
      setCreatingSupportCase(false);
    }
  };

  const noPartnersMessage = t(
    'chat.create.noPartners',
    'No eligible contacts found. You need at least one room or tutor booking in your organization with someone you do not already have a chat with.'
  );

  const adminEmptyTitle =
    adminTab === 'userSupport'
      ? t('chat.admin.userSupport.emptyTitle', 'No user support chats yet')
      : t('chat.admin.orgSupport.emptyTitle', 'No organization chats yet');
  const adminEmptyBody =
    adminTab === 'userSupport'
      ? t(
          'chat.admin.userSupport.emptyBody',
          'Individual support cases appear here when users contact Band Space.'
        )
      : t(
          'chat.admin.orgSupport.emptyBody',
          'Band Space Team threads appear here when hosts are enrolled for their organization.'
        );
  const adminSubtitle =
    adminTab === 'userSupport'
      ? t('chat.admin.userSupport.subtitle', 'Individual support cases from users.')
      : t('chat.admin.orgSupport.subtitle', 'Band Space Team threads with organization hosts.');

  return (
    <Box sx={chatPageSx}>
      <SEO title={adminInbox ? t('chat.admin.title', 'Support chats') : t('chat.title', 'Chats')} noindex />
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          pt: 2,
          pb: adminInbox ? 0 : 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'rgba(0,0,0,0.25)',
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ pb: adminInbox ? 1.5 : 0 }}>
          <Box>
            <Typography variant="h6" component="h1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
              {adminInbox ? t('chat.admin.title', 'Support chats') : t('chat.title', 'Chats')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {adminInbox
                ? adminSubtitle
                : t('chat.subtitle', 'Shared timeline, files, and library with your tutor or student.')}
            </Typography>
          </Box>
          {!adminInbox && (
            <Stack direction="row" spacing={1} flexShrink={0}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SupportAgentRoundedIcon />}
                onClick={openSupportCaseDialog}
              >
                {t('supportCases.create.action', 'Get help')}
              </Button>
              <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={openCreateDialog}>
                {t('chat.create.action', 'New chat')}
              </Button>
            </Stack>
          )}
        </Stack>
        {adminInbox && (
          <Tabs
            value={adminTab}
            onChange={(_, value: AdminChatTab) => setAdminTab(value)}
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': { height: 2 },
              '& .MuiTab-root': {
                minHeight: 40,
                py: 1,
                px: { xs: 1.25, sm: 2 },
                textTransform: 'none',
                fontWeight: 600,
              },
            }}
          >
            <Tab
              value="userSupport"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{t('chat.admin.userSupport.tab', 'User Support Chats')}</span>
                  {adminUserSupportUnread > 0 && (
                    <ChatUnreadBadge count={adminUserSupportUnread} size="list" />
                  )}
                </Stack>
              }
            />
            <Tab
              value="orgSupport"
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{t('chat.admin.orgSupport.tab', 'Organization Chats')}</span>
                  {adminOrgSupportUnread > 0 && (
                    <ChatUnreadBadge count={adminOrgSupportUnread} size="list" />
                  )}
                </Stack>
              }
            />
          </Tabs>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && visibleChats.length === 0 && (
          <Box sx={{ py: 8, px: 3, textAlign: 'center' }}>
            <ForumRoundedIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {adminInbox ? adminEmptyTitle : t('chat.list.emptyTitle', 'No chats yet')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 360, mx: 'auto' }}>
              {adminInbox
                ? adminEmptyBody
                : t(
                    'chat.list.emptyBody',
                    'Start a chat to share notes, files, and links with your tutor or student.'
                  )}
            </Typography>
            {!adminInbox && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <Button variant="outlined" startIcon={<SupportAgentRoundedIcon />} onClick={openSupportCaseDialog}>
                  {t('supportCases.create.action', 'Get help')}
                </Button>
                <Button variant="outlined" onClick={openCreateDialog}>
                  {t('chat.create.action', 'New chat')}
                </Button>
              </Stack>
            )}
          </Box>
        )}

            {!loading && !error && visibleChats.length > 0 && (
          <Stack spacing={0}>
            {visibleChats.map((chat, index) => {
              const orgChat = isOrganizationScopedChat(chat);
              const supportChat = isOrganizationSupportChat(chat);
              const supportCase = isSupportCaseChat(chat);
              const caseMeta = supportCase ? supportCaseByChatId(supportCases, chat.id) : undefined;
              const pinned = !adminInbox && isPinnedChat(chat);
              const lastPinned =
                pinned &&
                (index === visibleChats.length - 1 || !isPinnedChat(visibleChats[index + 1]));
              const pinnedLabel = t('chat.list.pinned', 'Pinned');
              const title = chatTitleLabel(chat, currentUserId, t, {
                inbox: adminInbox ? 'admin' : 'member',
                supportCaseSubject: supportCaseSubjectByChatId(supportCases, chat.id),
              });
              const partnerName = chatPartnerLabel(chat, currentUserId, t);
              const partnerUserId = chatCounterpartyUserId(chat, currentUserId);
              const members = chatParticipants(chat);
              const memberCountLabel = chatMemberCountLabel(chat, t);
              const unreadCount = chat.unreadCount ?? 0;
              const activityTime = formatChatTimestamp(chat.lastActivityAt, i18n.language);
              const subtitle =
                orgChat && memberCountLabel
                  ? `${memberCountLabel} · ${activityTime}`
                  : t('chat.list.lastActivity', 'Last activity {{time}}', { time: activityTime });

              return (
                <Box
                  key={chat.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`${basePath}/${chat.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`${basePath}/${chat.id}`);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    cursor: 'pointer',
                    borderBottom: lastPinned ? 2 : 1,
                    borderColor: lastPinned ? 'rgba(255,255,255,0.16)' : 'divider',
                    ...chatUnreadRowSx(unreadCount > 0),
                    '&:hover': unreadCount > 0
                      ? { bgcolor: 'rgba(255, 78, 113, 0.1)' }
                      : { bgcolor: 'rgba(255,255,255,0.05)' },
                    '&:focus-visible': {
                      outline: 2,
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  {orgChat ? (
                    <ChatMemberAvatars
                      members={members}
                      avatarByUserId={avatarByUserId}
                      max={3}
                      size={36}
                    />
                  ) : supportCase ? (
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'rgba(78, 103, 235, 0.22)',
                        color: 'primary.light',
                      }}
                    >
                      <SupportAgentRoundedIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                  ) : (
                    <Avatar
                      src={chatAvatarSrc(avatarByUserId, partnerUserId)}
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        bgcolor: chatAvatarColor(partnerUserId),
                      }}
                    >
                      {tutorAvatarInitials(partnerName)}
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                      {supportChat || supportCase ? (
                        <SupportAgentRoundedIcon
                          sx={{ fontSize: 14, color: 'primary.light', opacity: 0.9, flexShrink: 0 }}
                        />
                      ) : orgChat ? (
                        <GroupsRoundedIcon
                          sx={{ fontSize: 14, color: 'primary.light', opacity: 0.9, flexShrink: 0 }}
                        />
                      ) : (
                        <TagRoundedIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.8, flexShrink: 0 }} />
                      )}
                      <Typography
                        variant="subtitle2"
                        fontWeight={unreadCount > 0 ? 800 : 600}
                        noWrap
                        sx={{ minWidth: 0, color: unreadCount > 0 ? 'text.primary' : 'text.secondary' }}
                      >
                        {title}
                      </Typography>
                      {caseMeta && <SupportCaseStatusChip status={caseMeta.status} />}
                      {pinned && (
                        <PushPinRoundedIcon
                          aria-label={pinnedLabel}
                          titleAccess={pinnedLabel}
                          sx={{
                            fontSize: 13,
                            color: 'warning.main',
                            flexShrink: 0,
                            transform: 'rotate(45deg)',
                          }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                      {subtitle}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={1}
                    sx={{ flexShrink: 0, alignSelf: 'center', pl: 0.5 }}
                  >
                    <ChatUnreadBadge count={unreadCount} size="list" />
                    <ChevronRightRoundedIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>

      {!adminInbox && (
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: 2.25,
              borderBottom: 1,
              borderColor: 'divider',
              background: 'linear-gradient(135deg, rgba(78, 103, 235, 0.16), rgba(78, 103, 235, 0.03))',
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(78, 103, 235, 0.18)',
                color: 'primary.light',
                flexShrink: 0,
              }}
            >
              <ForumRoundedIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" component="span" fontWeight={750}>
                {t('chat.create.title', 'Start a chat')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {t('chat.create.dialogSubtitle', 'Choose a recipient to start a shared conversation.')}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('common.close', 'Close')}
              onClick={() => setCreateOpen(false)}
              disabled={creating}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pb: 2.5,
            // MUI sets padding-top: 0 when DialogContent follows DialogTitle — override it.
            '.MuiDialogTitle-root + &': { pt: 1.5 },
          }}
        >
          <Stack spacing={2}>
            {partnersLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!partnersLoading && partnersError && <Alert severity="error">{partnersError}</Alert>}

            {!partnersLoading && !partnersError && partnerOptions.length === 0 && (
              <Alert severity="info">{noPartnersMessage}</Alert>
            )}

            {!partnersLoading && !partnersError && partnerOptions.length > 0 && (
              <ChatPartnerPicker
                partners={partnerOptions}
                value={selectedPartner}
                onChange={setSelectedPartner}
                disabled={creating}
              />
            )}

            {createError && <Alert severity="error">{createError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'rgba(255,255,255,0.02)',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            '& > :not(style) ~ :not(style)': { ml: 0 },
          }}
        >
          <Button
            variant="text"
            onClick={() => setCreateOpen(false)}
            disabled={creating}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<ForumRoundedIcon />}
            onClick={() => void handleCreate()}
            disabled={creating || partnersLoading || !selectedPartner}
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 132 } }}
          >
            {creating ? t('common.loading', 'Loading...') : t('chat.create.submit', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      <Dialog
        open={supportCaseOpen}
        onClose={() => !creatingSupportCase && setSupportCaseOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: 2.25,
              borderBottom: 1,
              borderColor: 'divider',
              background: 'linear-gradient(135deg, rgba(78, 103, 235, 0.16), rgba(78, 103, 235, 0.03))',
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(78, 103, 235, 0.18)',
                color: 'primary.light',
                flexShrink: 0,
              }}
            >
              <SupportAgentRoundedIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" component="span" fontWeight={750}>
                {t('supportCases.create.title', 'Contact Band Space')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {t(
                  'supportCases.create.dialogSubtitle',
                  'Open a support case. Our team replies in this conversation.'
                )}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('common.close', 'Close')}
              onClick={() => setSupportCaseOpen(false)}
              disabled={creatingSupportCase}
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pb: 2.5,
            '.MuiDialogTitle-root + &': { pt: 1.5 },
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={t('supportCases.create.subject', 'Subject')}
              value={supportSubject}
              onChange={(event) => setSupportSubject(event.target.value)}
              required
              fullWidth
              autoFocus
              disabled={creatingSupportCase}
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label={t('supportCases.create.message', 'Message')}
              value={supportMessage}
              onChange={(event) => setSupportMessage(event.target.value)}
              required
              fullWidth
              multiline
              minRows={3}
              disabled={creatingSupportCase}
              inputProps={{ maxLength: 4000 }}
            />
            {supportCaseError && <Alert severity="error">{supportCaseError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            gap: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'rgba(255,255,255,0.02)',
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            '& > :not(style) ~ :not(style)': { ml: 0 },
          }}
        >
          <Button
            variant="text"
            onClick={() => setSupportCaseOpen(false)}
            disabled={creatingSupportCase}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SupportAgentRoundedIcon />}
            onClick={() => void handleCreateSupportCase()}
            disabled={creatingSupportCase || !supportSubject.trim() || !supportMessage.trim()}
            sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 148 } }}
          >
            {creatingSupportCase
              ? t('common.loading', 'Loading...')
              : t('supportCases.create.submit', 'Open case')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatListPage;
